import { useMemo } from 'react';

import {
  BatteryActivityType,
  BatteryCommand,
  ChartSegment,
  EnrichedForecastPoint,
  formatTimeFromUtcTimestamp,
  SegmentBoundaryMode,
  segmentByActivity,
} from '../../../utils/socChartUtils';

export type HistoricDataPoint = {
  x: string;
  y: number;
  _ts?: number;
  data?: {
    imported?: number | string | null;
    exported?: number | string | null;
    solar_generation?: number | string | null;
    start_date?: number | string | null;
    end_date?: number | string | null;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

const SOC_EPSILON = 0.1;
const FLOW_EPSILON = 0.0001;

function inferActivityType(
  deltaSoc: number,
  imported: number,
  exported: number,
  solarGeneration: number
): BatteryActivityType {
  if (deltaSoc > SOC_EPSILON) {
    return imported > FLOW_EPSILON ? 'gridCharge' : solarGeneration > FLOW_EPSILON ? 'solarCharge' : 'gridCharge';
  }
  if (deltaSoc < -SOC_EPSILON) {
    return exported > FLOW_EPSILON ? 'export' : 'consumption';
  }
  return 'idle';
}

function inferCommand(deltaSoc: number): BatteryCommand {
  if (deltaSoc > SOC_EPSILON) return 'charge';
  if (deltaSoc < -SOC_EPSILON) return 'discharge';
  return 'hold';
}

/**
 * Derives colour-coded chart segments from historic 24h system data.
 *
 * Activity type is inferred heuristically:
 *  - SOC rising + grid import → gridCharge
 *  - SOC rising + solar       → solarCharge
 *  - SOC falling + export     → export
 *  - SOC falling              → consumption
 *  - SOC flat                 → idle
 */
export const useHistoricChartSegments = (
  historicChartData: HistoricDataPoint[] | null | undefined,
  chartAvailable: boolean
): ChartSegment[] => {
  return useMemo(() => {
    if (!chartAvailable || !historicChartData?.length) return [];

    const enriched: EnrichedForecastPoint[] = historicChartData.map((point, index) => {
      const prevPoint = index > 0 ? historicChartData[index - 1] : null;
      const prevSoc = prevPoint?.y ?? point.y;
      const deltaSoc = point.y - prevSoc;

      const imported = Number(point.data?.imported ?? 0);
      const exported = Number(point.data?.exported ?? 0);
      const solarGeneration = Number(point.data?.solar_generation ?? 0);

      const activityType = inferActivityType(deltaSoc, imported, exported, solarGeneration);
      const command = inferCommand(deltaSoc);

      const startTs = point.data?.start_date;
      const endTs = point.data?.end_date;
      // Use start_date → end_date so hovering at x=11:30 shows "11:00 to 11:30"
      // (the interval that just ended), not the upcoming interval.
      const startTime = typeof startTs === 'number' ? formatTimeFromUtcTimestamp(startTs) : point.x;
      const endTime = typeof endTs === 'number' ? formatTimeFromUtcTimestamp(endTs) : '';

      return {
        x: point.x,
        y: point.y,
        // Historic data has a different shape to BehaviourForecastRecord; cast is intentional
        // as `data` is not accessed in tooltip rendering for historic points.
        data: point.data as unknown as EnrichedForecastPoint['data'],
        command,
        rateType: 'day',
        rate: 0,
        startTime,
        endTime,
        activityType,
        _previousSoc: prevSoc,
      };
    });

    // Historic x = end_date, so each point's activity covers the interval
    // [prev_end, curr_end]. Use 'prev' boundary mode so the colour change
    // starts at the previous tick (= start of the new activity's interval).
    return segmentByActivity(enriched, 'prev' as SegmentBoundaryMode);
  }, [chartAvailable, historicChartData]);
};
